import classNames from 'classnames';
import { type HTMLAttributes, useState } from 'react';

import styles from './CheckboxIndicator.module.css';

interface CheckboxIndicatorProps extends HTMLAttributes<HTMLSpanElement> {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  isHovered?: boolean;
  isFocused?: boolean;
  isActive?: boolean;
  /**
   * Overrides the internal "did this just get ticked" detection, for lists
   * whose rows remount on every selection change — react-arborist memoizes its
   * row on `{index, style}`, so the tree's renderer identity churn is what
   * makes a selection change render at all. A remounted indicator has no
   * memory of its previous state, so the owner (which does not remount) says
   * whether this row is the one that changed. Leave unset when rows persist.
   */
  animateCheck?: boolean;
}

/**
 * Same geometry as `ThemedCheckboxIcon`, so a consumer that passes no `icon`
 * (DataBuilder cards, report parameters) renders the identical tick to the
 * selection tree and the filter lists, which pass it explicitly.
 */
const CheckIcon = () => (
  <svg
    width="11"
    height="9.3"
    viewBox="0 0 13 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.41406 6.41109L4.41406 9.41109L11.4141 1.41109"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="square"
    />
  </svg>
);

const MinusIcon = () => (
  <svg
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.icon}
  >
    <path
      d="M3 6H9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const CheckboxIndicator = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  isHovered = false,
  isFocused = false,
  isActive = false,
  animateCheck,
  className,
  children,
  ...rest
}: CheckboxIndicatorProps) => {
  const showChecked = checked;
  const showIndeterminate = indeterminate && !checked;

  // Draw the tick only on a real unchecked→checked flip. Adjusting state
  // during render (rather than in an effect) means the animation class lands
  // in the same commit as `checked`, so the tick never flashes fully-drawn for
  // a frame first. Mounting leaves `wasChecked` equal to `checked`, so an
  // already-ticked box stays still while its siblings are clicked.
  const [wasChecked, setWasChecked] = useState(checked);
  const [detectedDraw, setDetectedDraw] = useState(false);
  if (checked !== wasChecked) {
    setWasChecked(checked);
    setDetectedDraw(checked);
  }
  // A remounting row destroys that state every toggle, so its owner decides.
  const isDrawing =
    animateCheck === undefined ? detectedDraw : animateCheck && showChecked;

  return (
    <span
      className={classNames(
        styles.indicator,
        showChecked && styles.checked,
        showIndeterminate && styles.indeterminate,
        isDrawing && styles.drawing,
        disabled && styles.disabled,
        isHovered && styles.hovered,
        isFocused && styles.focused,
        isActive && styles.active,
        className
      )}
      aria-hidden="true"
      data-checkbox-indicator
      data-checked={showChecked ? 'true' : undefined}
      data-indeterminate={showIndeterminate ? 'true' : undefined}
      {...rest}
    >
      <div className={styles.svgWrapper}>
        {(() => {
          // A caller-supplied glyph (e.g. the themed check/minus icon) takes
          // precedence over the built-in glyphs so the visual can be themed
          // without forking this indicator.
          if (children != null) {
            return children;
          }

          if (showChecked) {
            return <CheckIcon />;
          }

          if (showIndeterminate) {
            return <MinusIcon />;
          }

          return null;
        })()}
      </div>
    </span>
  );
};
